/**
 * LaunchPromoBanner — Bannière permanente "MamaLink vient de sortir"
 * Reste visible TANT QUE la maman n'a pas cliqué sur "Laisser un avis" (Play Store OU App Store).
 * Pas de bouton "X" ni "Plus tard" : seule l'action "Avis" la cache
 * définitivement (car c'est l'action qui aide vraiment l'app à se faire connaître).
 */
import React, { useState } from 'react';
import { Share2, Heart } from 'lucide-react';

// Clé v3 — nouvelle clé pour repartir de zéro (ajout App Store).
const STORAGE_KEY = 'mamalink_promo_reviewed_stores_v3';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=fr.mamalink.app';
const APP_STORE_URL = 'https://apps.apple.com/app/mamalink/id6753054000';
const SHARE_TEXT = "Je viens de découvrir MamaLink 💕 une appli d'entraide entre mamans : match, forums, conseils, bons plans, soutien... Viens la tester ! 👉 ";
const SHARE_URL = 'https://mamalink.fr';

const LaunchPromoBanner = () => {
  const [reviewed, setReviewed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  });

  // 🍎 Élodie #991 — Détection plateforme pour n'afficher QUE le bon store.
  // Bug remonté par Carole : sur Android, cliquer sur "App Store" ouvrait Apple → "App indisponible".
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/i.test(ua);
  // Si mobile identifié → un seul bouton store. Sinon (desktop/web) → les deux.
  const showPlayStore = !isIOS;   // Android + desktop voient Play Store
  const showAppStore = !isAndroid; // iOS + desktop voient App Store

  const markReviewed = () => {
    try { localStorage.setItem(STORAGE_KEY, 'true'); } catch (_) { /* ignore */ }
    setReviewed(true);
  };

  const handleNativeShare = async () => {
    const data = { title: 'MamaLink 💕', text: SHARE_TEXT, url: SHARE_URL };
    if (navigator.share) {
      try { await navigator.share(data); } catch (_) { /* user cancelled */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(`${SHARE_TEXT}${SHARE_URL}`);
      alert('Lien copié ! Tu peux maintenant le coller où tu veux 💕');
    } catch (_) {
      window.open(SHARE_URL, '_blank');
    }
  };

  const copyAndOpen = async (androidIntent, webUrl) => {
    try {
      await navigator.clipboard.writeText(`${SHARE_TEXT}${SHARE_URL}`);
    } catch (_) { /* clipboard refused, on continue quand même */ }
    const ua = navigator.userAgent || '';
    if (/Android/i.test(ua) && androidIntent) {
      window.location.href = androidIntent;
    } else {
      window.open(webUrl, '_blank');
    }
  };

  const handleOpenInstagram = () =>
    copyAndOpen(
      'intent://instagram.com/#Intent;scheme=https;package=com.instagram.android;S.browser_fallback_url=https%3A%2F%2Fwww.instagram.com%2F;end',
      'https://www.instagram.com/'
    );

  const handleOpenTikTok = () =>
    copyAndOpen(
      'intent://tiktok.com/#Intent;scheme=https;package=com.zhiliaoapp.musically;S.browser_fallback_url=https%3A%2F%2Fwww.tiktok.com%2F;end',
      'https://www.tiktok.com/'
    );

  const handlePlayStoreReview = () => {
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
    markReviewed();
  };

  const handleAppStoreReview = () => {
    window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
    markReviewed();
  };

  if (reviewed) return null;

  return (
    <div
      className="relative mb-4 overflow-hidden rounded-3xl bg-gradient-to-br from-[#FFB5A7] via-[#FCD5CE] to-[#FFE5D9] shadow-md"
      data-testid="launch-promo-banner"
    >
      {/* Soft floating shapes for depth */}
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/20 blur-2xl pointer-events-none" />
      <div className="absolute -bottom-8 -left-4 w-32 h-32 rounded-full bg-white/15 blur-2xl pointer-events-none" />

      <div className="relative px-5 pt-5 pb-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-white/40 backdrop-blur-sm flex items-center justify-center text-2xl shadow-sm">
            🎉
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-[#5C3D36] leading-snug">
              MamaLink vient de sortir&nbsp;!
            </h3>
            <p className="mt-1.5 text-[13px] text-[#5C3D36]/85 leading-relaxed">
              Aide-nous à nous faire connaître : partage MamaLink à tes copines mamans
              et laisse un petit avis ⭐ sur ton store, ça fait une vraie différence{' '}
              <Heart className="w-3.5 h-3.5 inline text-[#E63946] fill-[#E63946] -mt-0.5" />
            </p>
          </div>
        </div>

        {/* Action buttons — 3 ou 4 colonnes selon plateforme : Instagram, TikTok, Play Store et/ou App Store */}
        <div className={`grid ${showPlayStore && showAppStore ? 'grid-cols-4' : 'grid-cols-3'} gap-2 mt-4`}>
          <button
            onClick={handleOpenInstagram}
            className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-2xl bg-white/70 backdrop-blur-sm hover:bg-white transition-all active:scale-95 shadow-sm"
            data-testid="launch-promo-instagram"
          >
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor" aria-hidden="true">
                <path d="M12 2.2c3.2 0 3.6 0 4.8.1 1.2.1 1.8.2 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.2.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 1.2-.2 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.2.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-1.2-.1-1.8-.2-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8c.1-1.2.2-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2zM12 0C8.7 0 8.3 0 7.1.1 5.8.1 5 .3 4.2.6c-.8.3-1.5.7-2.2 1.4C1.3 2.7.9 3.4.6 4.2.3 5 .1 5.8.1 7.1 0 8.3 0 8.7 0 12s0 3.7.1 4.9c.1 1.3.3 2.1.6 2.9.3.8.7 1.5 1.4 2.2.7.7 1.4 1.1 2.2 1.4.8.3 1.6.5 2.9.6C8.3 24 8.7 24 12 24s3.7 0 4.9-.1c1.3-.1 2.1-.3 2.9-.6.8-.3 1.5-.7 2.2-1.4.7-.7 1.1-1.4 1.4-2.2.3-.8.5-1.6.6-2.9.1-1.2.1-1.6.1-4.9s0-3.7-.1-4.9c-.1-1.3-.3-2.1-.6-2.9-.3-.8-.7-1.5-1.4-2.2C21.3 1.3 20.6.9 19.8.6 19 .3 18.2.1 16.9.1 15.7 0 15.3 0 12 0zm0 5.8a6.2 6.2 0 1 0 0 12.4A6.2 6.2 0 0 0 12 5.8zm0 10.2a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-11.8a1.4 1.4 0 1 0 0 2.9 1.4 1.4 0 0 0 0-2.9z" />
              </svg>
            </div>
            <span className="text-[10px] font-semibold text-[#5C3D36] leading-tight">Instagram</span>
          </button>

          <button
            onClick={handleOpenTikTok}
            className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-2xl bg-white/70 backdrop-blur-sm hover:bg-white transition-all active:scale-95 shadow-sm"
            data-testid="launch-promo-tiktok"
          >
            <div className="w-7 h-7 rounded-lg bg-black flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
                <path fill="#FF0050" d="M19.32 5.55a5.43 5.43 0 0 1-3.18-1.94 5.4 5.4 0 0 1-1.22-3.6h-3.18v13.74a2.85 2.85 0 0 1-2.85 2.85 2.85 2.85 0 0 1 0-5.7c.32 0 .63.06.92.16V7.86a6.04 6.04 0 1 0 5.07 5.95V8.07a8.56 8.56 0 0 0 4.44 1.24V5.84a5.5 5.5 0 0 1 0-.29z" />
                <path fill="#00F2EA" d="M18.32 6.05a5.43 5.43 0 0 1-3.18-1.94 5.4 5.4 0 0 1-1.22-3.6h-2.18v13.74a2.85 2.85 0 0 1-2.85 2.85 2.85 2.85 0 0 1-1-5.54v-3.3a6.04 6.04 0 1 0 4.07 5.74V8.57a8.56 8.56 0 0 0 4.44 1.24V6.34a5.5 5.5 0 0 1-.08-.29z" />
                <path fill="#fff" d="M18.32 6.05a5.43 5.43 0 0 1-3.18-1.94 5.4 5.4 0 0 1-1.22-3.6h-2.18v13.74a2.85 2.85 0 0 1-2.85 2.85 2.85 2.85 0 0 1-1-5.54v-3.3a6.04 6.04 0 1 0 4.07 5.74V8.57a8.56 8.56 0 0 0 4.44 1.24V6.34a5.5 5.5 0 0 1-.08-.29z" opacity=".2" />
              </svg>
            </div>
            <span className="text-[10px] font-semibold text-[#5C3D36] leading-tight">TikTok</span>
          </button>

          {showPlayStore && (
          <button
            onClick={handlePlayStoreReview}
            className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-2xl bg-white hover:bg-white/95 transition-all active:scale-95 shadow-sm ring-2 ring-[#FFB347]/40"
            data-testid="launch-promo-playstore"
            title="Laisser un avis sur Google Play"
          >
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#01875F] via-[#00A566] to-[#01875F] flex items-center justify-center">
              {/* Google Play icon */}
              <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
                <path fill="#fff" d="M3.7 20.6l10-10-10-10c-.5.2-.7.6-.7 1.2v17.7c0 .6.2 1 .7 1.1zm11.2-8.9l2.7 2.7-11.7 6.6 9-9.3zm4.9-3.4l-3.3 1.9-2.9 2.9 2.9 2.9 3.3 1.9c1-.5 1-2 0-2.5l-1.5-.9 1.5-.9c1-.5 1-2 0-2.4zM5.9 3.4l11.7 6.6-2.7 2.7-9-9.3z"/>
              </svg>
            </div>
            <span className="text-[10px] font-bold text-[#5C3D36] leading-tight">Play Store</span>
          </button>
          )}

          {showAppStore && (
          <button
            onClick={handleAppStoreReview}
            className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-2xl bg-white hover:bg-white/95 transition-all active:scale-95 shadow-sm ring-2 ring-[#A2D2FF]/40"
            data-testid="launch-promo-appstore"
            title="Laisser un avis sur l'App Store"
          >
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#000] via-[#333] to-[#000] flex items-center justify-center">
              {/* Apple logo */}
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor" aria-hidden="true">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
            </div>
            <span className="text-[10px] font-bold text-[#5C3D36] leading-tight">App Store</span>
          </button>
          )}
        </div>

        {/* Native share secondary */}
        <div className="mt-3 flex items-center justify-center">
          <button
            onClick={handleNativeShare}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#5C3D36] text-white text-xs font-semibold hover:opacity-90 transition-opacity"
            data-testid="launch-promo-native-share"
          >
            <Share2 className="w-3.5 h-3.5" />
            Partager via une autre app
          </button>
        </div>
      </div>
    </div>
  );
};

export default LaunchPromoBanner;
